import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'cirun:team-scope';

const TeamScopeContext = createContext(null);

/**
 * TeamScopeProvider holds the global "currently-selected team" — empty string
 * means the user's personal team, any non-empty value is an org team UUID.
 *
 * Every protected page reads this via `useTeamScope()` so the dashboard,
 * cloud-credentials page, repositories page, and any future team-scoped view
 * stay in sync. Selection persists in localStorage across refreshes.
 *
 * The provider is intentionally dumb: it does not validate that the UUID is
 * a team the user is a member of — the backend rejects unauthorised writes
 * with 403/404. Validation here would couple the provider to data fetching
 * and force every page to wait on the teams query.
 */
export function TeamScopeProvider({ children }) {
  const [teamUuid, setTeamUuidState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) ?? '';
    } catch {
      return '';
    }
  });

  const setTeamUuid = useCallback((next) => {
    setTeamUuidState(next ?? '');
    try {
      if (next) {
        localStorage.setItem(STORAGE_KEY, next);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // ignore quota / private-mode failures
    }
  }, []);

  // Sync across browser tabs so changing the team in one tab updates others.
  useEffect(() => {
    const handler = (event) => {
      if (event.key === STORAGE_KEY) {
        setTeamUuidState(event.newValue ?? '');
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const value = useMemo(() => ({ teamUuid, setTeamUuid }), [teamUuid, setTeamUuid]);

  return <TeamScopeContext.Provider value={value}>{children}</TeamScopeContext.Provider>;
}

export function useTeamScope() {
  const ctx = useContext(TeamScopeContext);
  if (!ctx) {
    throw new Error('useTeamScope must be used inside <TeamScopeProvider>');
  }
  return ctx;
}
